import React, { useMemo, useState } from 'react';
import { SalvButton, SalvRadioToggleButton, SalvRadioToggleGroup, Size } from '@salvapp/ui-components';
import { useFinalRiskHistory, useFinalRiskRanges } from './riskActions';
import RiskHistoryChart from './RiskHistoryChart';
import RiskHistoryTable from './RiskHistoryTable';
import {
  DEFAULT_RISK_HISTORY_DATE_RANGE,
  DEFAULT_RISK_HISTORY_EVENT_TYPE,
  filterRiskHistory,
  RiskHistoryDateRange,
  RiskHistoryEventType,
} from './riskHistoryFilters';
import ExportModal from '../search/ExportModal';
import { InternalApiExportFileType } from '../api-internal';
import { riskApi } from '../apis';

const DATE_RANGE_OPTIONS: { value: RiskHistoryDateRange; label: string }[] = [
  { value: '7', label: '7D' },
  { value: '30', label: '30D' },
  { value: '90', label: '90D' },
  { value: 'ALL', label: 'All' },
];

const EVENT_TYPE_OPTIONS: { value: RiskHistoryEventType; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'SCORE_CHANGE', label: 'Score events' },
  { value: 'OVERRIDE', label: 'Overrides' },
];

interface Props {
  personId: string;
}

export const RiskHistory = ({ personId }: Props) => {
  const [dateRange, setDateRange] = useState<RiskHistoryDateRange>(DEFAULT_RISK_HISTORY_DATE_RANGE);
  const [eventType, setEventType] = useState<RiskHistoryEventType>(DEFAULT_RISK_HISTORY_EVENT_TYPE);
  const [showExportModal, setShowExportModal] = useState(false);

  const { data, isLoading, isError } = useFinalRiskHistory(personId);
  const { data: ranges } = useFinalRiskRanges();

  const events = useMemo(() => data ?? [], [data]);
  const tableEvents = useMemo(() => filterRiskHistory(events, dateRange, eventType), [events, dateRange, eventType]);

  const exportRiskHistory = async (fileType: InternalApiExportFileType) =>
    await riskApi.exportFinalRiskHistory({ fileType, personId });

  return (
    <>
      <div className='risk-history-filters'>
        <SalvRadioToggleGroup
          size={Size.SMALL}
          ariaLabel='Date range'
          value={dateRange}
          onChange={(value) => setDateRange(value as RiskHistoryDateRange)}
        >
          {DATE_RANGE_OPTIONS.map((option) => (
            <SalvRadioToggleButton key={option.value} value={option.value} label={option.label} />
          ))}
        </SalvRadioToggleGroup>
        <SalvButton label={'Export'} variant={'secondary'} onClick={() => setShowExportModal(true)} size={Size.SMALL} />
      </div>
      <div className='risk-chart'>
        <RiskHistoryChart events={events} ranges={ranges} dateRange={dateRange} />
      </div>
      <div className='risk-history-filters'>
        <SalvRadioToggleGroup
          size={Size.SMALL}
          ariaLabel='Event type'
          value={eventType}
          onChange={(value) => setEventType(value as RiskHistoryEventType)}
        >
          {EVENT_TYPE_OPTIONS.map((option) => (
            <SalvRadioToggleButton key={option.value} value={option.value} label={option.label} />
          ))}
        </SalvRadioToggleGroup>
      </div>
      <RiskHistoryTable events={tableEvents} isLoading={isLoading} isError={isError} />
      <ExportModal show={showExportModal} setShow={setShowExportModal} exportEntities={exportRiskHistory} synchronous />
    </>
  );
};

export default RiskHistory;
